import React, { useState, useCallback } from "react";
import SearchService from "../../services/SearchService";
import "./SearchBar.css";

/**
 * SearchBar Component
 * Componente de barra de pesquisa com auto-complete
 * @version 1.0.0
 */

const SearchBar = ({ onSearch }) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);

  // Debounced search for suggestions
  const fetchSuggestions = useCallback(async (searchTerm) => {
    if (searchTerm.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const suggestionsData = await SearchService.getSearchSuggestions(
        searchTerm,
        8
      );
      setSuggestions(suggestionsData);
    } catch (err) {
      console.error("Erro ao buscar sugestões:", err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Implementar debounce
  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setQuery(value);

      if (value.trim().length > 0) {
        setShowSuggestions(true);
        const timeoutId = setTimeout(() => {
          fetchSuggestions(value);
        }, 300);
        return () => clearTimeout(timeoutId);
      } else {
        setShowSuggestions(false);
        setSuggestions([]);
      }
    },
    [fetchSuggestions]
  );

  // Realizar busca
  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query);
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  // Selecionar sugestão
  const handleSelectSuggestion = (suggestion) => {
    setQuery(suggestion);
    onSearch(suggestion);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Limpar busca
  const handleClear = () => {
    setQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className="search-bar-container">
      <form onSubmit={handleSearch} className="search-bar-form">
        <div className="search-bar-wrapper">
          <span className="search-icon">🔍</span>

          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => query.length > 0 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Buscar exercícios, usuários, blogs..."
            className="search-input"
            autoComplete="off"
          />

          {query && (
            <button
              type="button"
              onClick={handleClear}
              className="clear-btn"
              title="Limpar pesquisa"
            >
              ✕
            </button>
          )}

          <button type="submit" className="search-btn">
            Pesquisar
          </button>
        </div>

        {/* Auto-complete / Sugestões */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="suggestions-dropdown">
            {loading && <div className="suggestion-loading">Carregando...</div>}

            {suggestions.map((suggestion, idx) => (
              <div
                key={idx}
                className="suggestion-item"
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                <span className="suggestion-icon">🔍</span>
                <span className="suggestion-text">{suggestion}</span>
              </div>
            ))}
          </div>
        )}

        {showSuggestions && query.length > 0 && suggestions.length === 0 && !loading && (
          <div className="suggestions-dropdown">
            <div className="suggestion-empty">Nenhuma sugestão encontrada</div>
          </div>
        )}
      </form>

      {/* Quick Tips */}
      <div className="search-tips">
        <p>💡 Dica: Use aspas para buscar termos exatos: "exercício abdominal"</p>
      </div>
    </div>
  );
};

export default SearchBar;
